/**
 * OTG247 — Frequently Bought Together Block
 *
 * Integrates with FoxKit theme's cart drawer pattern:
 *   1. POST /cart/add.js to add items
 *   2. Fetch updated /cart.js for complete cart data
 *   3. Publish to FoxTheme.pubsub (cartUpdate) → updates cart line items
 *   4. Dispatch 'cart:refresh' event with {open: true} → opens drawer
 */

(function () {
  'use strict';

  if (window.__fbtBlockInit) return;
  window.__fbtBlockInit = true;

  function initAll() {
    const blocks = document.querySelectorAll('[data-fbt-block]');
    blocks.forEach(initBlock);
  }

  function initBlock(block) {
    if (block.dataset.fbtInitialized === 'true') return;
    block.dataset.fbtInitialized = 'true';

    const checkboxes = block.querySelectorAll('[data-fbt-checkbox]');
    const addButton = block.querySelector('[data-fbt-add-button]');
    const totalPriceEl = block.querySelector('[data-fbt-total-price]');
    const buttonLabelEl = block.querySelector('[data-fbt-button-label]');

    if (!addButton || !totalPriceEl) return;

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        updateTotal(block, totalPriceEl, buttonLabelEl, addButton);
      });
    });

    addButton.addEventListener('click', function (e) {
      e.preventDefault();
      handleAddToCart(block, addButton, buttonLabelEl);
    });

    updateTotal(block, totalPriceEl, buttonLabelEl, addButton);
  }

  function getSelectedItems(block) {
    const items = block.querySelectorAll('[data-fbt-item]');
    const selected = [];
    items.forEach(function (item) {
      const cb = item.querySelector('[data-fbt-checkbox]');
      if (cb && cb.checked && !cb.disabled) {
        selected.push({
          productId: item.dataset.productId,
          variantId: item.dataset.variantId,
          price: parseInt(item.dataset.price, 10) || 0
        });
      }
    });
    return selected;
  }

  function updateTotal(block, totalPriceEl, buttonLabelEl, addButton) {
    const selected = getSelectedItems(block);
    const totalCents = selected.reduce(function (sum, item) {
      return sum + item.price;
    }, 0);

    totalPriceEl.textContent = formatMoney(totalCents);
    addButton.dataset.selectedCount = String(selected.length);

    if (selected.length === 0) {
      addButton.disabled = true;
      if (buttonLabelEl) buttonLabelEl.textContent = 'Select items to add';
    } else {
      addButton.disabled = false;
      if (buttonLabelEl) {
        const label = selected.length === 1
          ? 'Add 1 item to cart'
          : 'Add ' + selected.length + ' items to cart';
        buttonLabelEl.textContent = label;
      }
    }
  }

  function handleAddToCart(block, addButton, buttonLabelEl) {
    const selected = getSelectedItems(block);
    if (selected.length === 0) return;

    const originalLabel = buttonLabelEl ? buttonLabelEl.textContent : '';
    addButton.disabled = true;
    if (buttonLabelEl) buttonLabelEl.textContent = 'Adding...';

    const uniqueBase = Date.now().toString(36) + Math.random().toString(36).substring(2, 8);

    const items = selected.map(function (item, i) {
      return {
        id: parseInt(item.variantId, 10),
        quantity: 1,
        properties: {
          _fbt_source: block.dataset.sourceProductId,
          _unique_id: uniqueBase + '-' + i
        }
      };
    });

    // Step 1: gather the section IDs the cart drawer wants to re-render
    // (FoxKit pattern: dispatch 'cart:grouped-sections' with a sections array,
    // listeners push their own section IDs into that array)
    const sectionsToRender = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
      bubbles: true,
      detail: { sections: sectionsToRender }
    }));

    // Step 2: POST /cart/add.js with items AND sections — Shopify returns the
    // rendered sections HTML which the cart-items component then swaps in
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: sectionsToRender
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || err.message || 'Could not add to cart');
          });
        }
        return res.json();
      })
      .then(function () {
        // Step 3: fetch the full cart.js so we have complete cart data
        // (sections returned from /cart/add.js don't include totals, item_count, etc.)
        return fetch('/cart.js?' + Date.now(), {
          headers: { 'Accept': 'application/json' }
        }).then(function (r) { return r.json(); });
      })
      .then(function (cartData) {
        // Step 4: re-fetch with sections so cart-items can swap innerHTML
        // (FoxKit's CartItems.onCartUpdate expects cart.sections populated)
        return fetch('/cart.js?sections=' + sectionsToRender.join(','), {
          headers: { 'Accept': 'application/json' }
        }).then(function (r) { return r.json(); }).then(function (cartWithSections) {
          // Merge: cartData has totals, cartWithSections has .sections
          cartData.sections = cartWithSections.sections || {};
          return cartData;
        });
      })
      .then(function (cartData) {
        // Step 5: publish cart update to FoxKit's pub/sub
        // This triggers CartItems.onCartUpdate which swaps in the new HTML
        if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
          try {
            window.FoxTheme.pubsub.publish(
              window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { source: 'fbt-block', cart: cartData }
            );
          } catch (e) { console.warn('FBT: pubsub publish failed', e); }
        }

        // Step 6: dispatch cart:refresh with open:true
        // This triggers CartDrawer.onCartRefresh which re-fetches and opens the drawer
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { open: true }
        }));

        // Success UI
        if (buttonLabelEl) buttonLabelEl.textContent = 'Added!';
        setTimeout(function () {
          if (buttonLabelEl) buttonLabelEl.textContent = originalLabel;
          addButton.disabled = false;
        }, 1500);
      })
      .catch(function (err) {
        console.error('FBT add-to-cart failed:', err);
        if (buttonLabelEl) buttonLabelEl.textContent = 'Error - try again';
        setTimeout(function () {
          if (buttonLabelEl) buttonLabelEl.textContent = originalLabel;
          addButton.disabled = false;
        }, 2000);
      });
  }

  function formatMoney(cents) {
    // Prefer FoxKit's formatter if available
    if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
      const fmt = (window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat) || undefined;
      return window.FoxTheme.Currency.formatMoney(cents, fmt);
    }
    // Fallback
    const formatString = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return formatString
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
})();
